import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { useWishlist } from '@/contexts/WishlistContext';
import { Button } from '@/components/ui/button';
import { Loader2, ShoppingCart, Minus, Plus, ChevronLeft, ChevronRight, Heart, ZoomIn, X, Share2, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewText, setReviewText] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifySubmitting, setNotifySubmitting] = useState(false);
  const [notifySuccess, setNotifySuccess] = useState('');
  const { isWishlisted, toggleWishlist } = useWishlist();

  // Zoom state
  const [hoverZoom, setHoverZoom] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxScale, setLightboxScale] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const imgContainerRef = useRef(null);
  const touchStateRef = useRef({ initialDist: 0, initialScale: 1, initialPan: { x: 0, y: 0 }, initialTouch: { x: 0, y: 0 }, lastTap: 0 });

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);
      // Fetch related products from same category (exclude current)
      if (response.data.category) {
        try {
          const rel = await api.get(`/products?category=${encodeURIComponent(response.data.category)}`);
          setRelatedProducts((rel.data || []).filter(p => p.id !== response.data.id).slice(0, 4));
        } catch (_) { /* soft-fail */ }
      }
      // Fetch approved reviews
      try {
        const r = await api.get(`/products/${response.data.id}/reviews`);
        setReviews(r.data || []);
      } catch (_) { /* soft-fail */ }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = `${BACKEND_URL}/api/share/product/${id}`;
    const shareData = {
      title: product?.name || 'Bandhani Heritage',
      text: `Check out this ${product?.category || 'product'} from Bandhani Heritage: ${product?.name}`,
      url: shareUrl
    };
    // Try native share (mobile)
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setShareCopied(false), 2000);
    } catch (_) {
      toast.error('Could not copy link');
    }
  };

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    const email = notifyEmail.trim();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    try {
      setNotifySubmitting(true);
      const resp = await api.post(`/products/${id}/notify`, { email });
      setNotifySuccess(resp.data.message || "You'll be notified!");
      setNotifyEmail('');
      toast.success(resp.data.message || "You're on the waitlist!");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setNotifySubmitting(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    const text = reviewText.trim();
    if (text.length < 5) {
      toast.error('Please write at least 5 characters.');
      return;
    }
    if (!user) {
      toast.error('Please login to write a review');
      navigate('/login');
      return;
    }
    try {
      setReviewSubmitting(true);
      const resp = await api.post(`/products/${id}/reviews`, { text });
      toast.success(resp.data.message);
      setReviewText('');
      setReviewSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }
    try {
      setAdding(true);
      await addToCart(product.id, quantity);
      toast.success('Added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="product-detail-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!product) return null;

  const allImages = [product.image, ...(product.images || [])].filter(Boolean);
  const currentImage = allImages[selectedImage] || product.image || 'https://placehold.co/800x1000/f5f5f4/a8a29e?text=No+Image';

  // SEO meta values (react-helmet-async needs plain string children)
  const pageTitle = `${product.name} — Bandhani Heritage`;
  const pageDescription = (product.description || '').slice(0, 200);
  const ogImage = product.image && product.image.startsWith('/')
    ? `${BACKEND_URL}${product.image}`
    : (product.image || '');
  const pageUrl = `${window.location.origin}/products/${product.id}`;

  const handleMouseMove = (e) => {
    if (!imgContainerRef.current) return;
    const rect = imgContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  const openLightbox = () => {
    setLightboxScale(1);
    setPanOffset({ x: 0, y: 0 });
    setLightboxOpen(true);
  };

  const resetLightbox = () => {
    setLightboxScale(1);
    setPanOffset({ x: 0, y: 0 });
  };

  // Mobile pinch-to-zoom + pan + double-tap handlers
  const getDistance = (t1, t2) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Pinch start
      touchStateRef.current.initialDist = getDistance(e.touches[0], e.touches[1]);
      touchStateRef.current.initialScale = lightboxScale;
    } else if (e.touches.length === 1) {
      // Pan start or double-tap detect
      const now = Date.now();
      const timeSinceLastTap = now - (touchStateRef.current.lastTap || 0);
      if (timeSinceLastTap > 0 && timeSinceLastTap < 300) {
        // Double tap toggles zoom
        if (lightboxScale > 1) {
          setLightboxScale(1);
          setPanOffset({ x: 0, y: 0 });
        } else {
          setLightboxScale(2.5);
        }
        touchStateRef.current.lastTap = 0;
      } else {
        touchStateRef.current.lastTap = now;
      }
      touchStateRef.current.initialTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      touchStateRef.current.initialPan = { ...panOffset };
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const newDist = getDistance(e.touches[0], e.touches[1]);
      const { initialDist, initialScale } = touchStateRef.current;
      if (initialDist > 0) {
        const newScale = Math.max(1, Math.min(4, initialScale * (newDist / initialDist)));
        setLightboxScale(newScale);
        if (newScale === 1) setPanOffset({ x: 0, y: 0 });
      }
    } else if (e.touches.length === 1 && lightboxScale > 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - touchStateRef.current.initialTouch.x;
      const dy = e.touches[0].clientY - touchStateRef.current.initialTouch.y;
      setPanOffset({
        x: touchStateRef.current.initialPan.x + dx,
        y: touchStateRef.current.initialPan.y + dy
      });
    }
  };

  return (
    <div className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8">
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta property="og:type" content="product" />
      <meta property="og:site_name" content="Bandhani Heritage" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={pageUrl} />
      <meta property="product:price:amount" content={String(product.price)} />
      <meta property="product:price:currency" content="INR" />
      <meta name="twitter:card" content="summary_large_image" />
      <link rel="canonical" href={pageUrl} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
        {/* Image Gallery */}
        <div data-testid="product-image-container">
          <div
            ref={imgContainerRef}
            className="relative h-[500px] md:h-[600px] overflow-hidden rounded-xl mb-4 cursor-zoom-in group bg-secondary/30"
            onMouseEnter={() => setHoverZoom(true)}
            onMouseLeave={() => setHoverZoom(false)}
            onMouseMove={handleMouseMove}
            onClick={openLightbox}
            data-testid="product-zoom-container"
          >
            <img
              src={currentImage}
              alt={product.name}
              onError={(e) => { e.currentTarget.src = 'https://placehold.co/800x1000/f5f5f4/a8a29e?text=Image+Unavailable'; }}
              className="w-full h-full object-cover rounded-xl transition-transform duration-200 ease-out select-none"
              style={hoverZoom ? {
                transform: 'scale(2)',
                transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`
              } : undefined}
              draggable={false}
              data-testid="product-main-image"
            />
            {/* Zoom hint badge */}
            <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs uppercase tracking-widest flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <ZoomIn className="h-3.5 w-3.5" />
              Hover to zoom • Click for full view
            </div>
            {allImages.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full shadow-md z-10"
                  onClick={(e) => { e.stopPropagation(); setSelectedImage((selectedImage - 1 + allImages.length) % allImages.length); }}
                  data-testid="image-prev-button"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full shadow-md z-10"
                  onClick={(e) => { e.stopPropagation(); setSelectedImage((selectedImage + 1) % allImages.length); }}
                  data-testid="image-next-button"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-3 py-1 rounded-full z-10 pointer-events-none">
                  {selectedImage + 1} / {allImages.length}
                </div>
              </>
            )}
          </div>
          {allImages.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="image-thumbnails">
              {allImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    selectedImage === idx ? 'border-primary ring-2 ring-primary/30' : 'border-border opacity-70 hover:opacity-100'
                  }`}
                  data-testid={`image-thumbnail-${idx}`}
                >
                  <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col justify-center">
          <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-4">{product.category}</p>
          <h1 className="text-4xl md:text-5xl font-normal tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="product-name">
            {product.name}
          </h1>
          <p className="text-3xl font-light mb-4" data-testid="product-price">₹{product.price.toLocaleString('en-IN')}</p>
          
          <button
            onClick={() => {
              if (!user) { toast.error('Please login to add to wishlist'); navigate('/login'); return; }
              toggleWishlist(product.id).then(ok => {
                if (ok) toast.success(isWishlisted(product.id) ? 'Removed from wishlist' : 'Added to wishlist');
              });
            }}
            className="flex items-center gap-2 mb-4 text-sm uppercase tracking-widest hover:text-red-500 transition-colors"
            data-testid="product-wishlist-btn"
          >
            <Heart className={`h-5 w-5 transition-colors ${isWishlisted(product.id) ? 'fill-red-500 text-red-500' : ''}`} />
            {isWishlisted(product.id) ? 'Saved to Wishlist' : 'Add to Wishlist'}
          </button>

          <button
            onClick={handleShare}
            className="flex items-center gap-2 mb-8 text-sm uppercase tracking-widest hover:text-primary transition-colors"
            data-testid="product-share-btn"
          >
            {shareCopied ? <Check className="h-5 w-5 text-green-600" /> : <Share2 className="h-5 w-5" />}
            {shareCopied ? 'Link Copied!' : 'Share This Product'}
          </button>
          
          <p className="text-lg leading-relaxed font-light mb-8" data-testid="product-description">
            {product.description}
          </p>

          {product.stock > 0 ? (
            <>
              <div className="flex items-center gap-4 mb-8">
                <span className="text-sm uppercase tracking-widest">Quantity:</span>
                <div className="flex items-center border border-border">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="rounded-full"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    data-testid="quantity-decrease-button"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="px-6" data-testid="product-quantity">{quantity}</span>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="rounded-full"
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    data-testid="quantity-increase-button"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <span className="text-sm text-muted-foreground">{product.stock} in stock</span>
              </div>

              <Button 
                onClick={handleAddToCart}
                disabled={adding}
                className="w-full md:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 uppercase tracking-widest text-sm font-medium"
                data-testid="add-to-cart-button"
              >
                {adding ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    Add to Cart
                  </>
                )}
              </Button>
            </>
          ) : (
            <div className="mb-8" data-testid="out-of-stock-message">
              <div className="bg-secondary p-6 rounded-xl mb-4 border border-border">
                <p className="text-center text-sm uppercase tracking-widest mb-1">Currently Out of Stock</p>
                <p className="text-center text-xs text-muted-foreground">
                  Get notified the moment this piece is back in our collection.
                </p>
              </div>
              {notifySuccess ? (
                <div className="bg-green-50 border border-green-200 p-4 rounded-xl flex items-start gap-3" data-testid="notify-success">
                  <Check className="h-5 w-5 text-green-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-900">
                      {notifySuccess.toLowerCase().includes('already') ? 'Already on the list' : "You're on the list!"}
                    </p>
                    <p className="text-xs text-green-800 mt-0.5">{notifySuccess}</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleNotifySubmit} className="flex flex-col sm:flex-row gap-2" data-testid="notify-form">
                  <input
                    type="email"
                    required
                    placeholder="your@email.com"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    className="flex-1 px-4 py-3 border border-input bg-background rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    data-testid="notify-email-input"
                    disabled={notifySubmitting}
                  />
                  <Button
                    type="submit"
                    disabled={notifySubmitting}
                    className="rounded-full px-6 py-3 uppercase tracking-widest text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 whitespace-nowrap"
                    data-testid="notify-submit-btn"
                  >
                    {notifySubmitting ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Adding...</>
                    ) : (
                      'Notify Me'
                    )}
                  </Button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="mt-20 md:mt-28 border-t border-border pt-16" data-testid="related-products-section">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">More from {product.category}</p>
              <h2 className="text-3xl md:text-4xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                You May Also Like
              </h2>
            </div>
            <Link
              to={`/products?category=${encodeURIComponent(product.category)}`}
              className="hidden md:inline-block text-xs uppercase tracking-widest border-b border-current pb-1 hover:text-primary transition-colors"
              data-testid="related-view-all"
            >
              View All →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {relatedProducts.map((rp) => (
              <Link
                key={rp.id}
                to={`/products/${rp.id}`}
                className="group block"
                data-testid={`related-product-${rp.id}`}
              >
                <div className="relative overflow-hidden mb-3 h-[240px] md:h-[320px] rounded-xl bg-secondary/30">
                  <img
                    src={rp.image || 'https://placehold.co/600x800/f5f5f4/a8a29e?text=No+Image'}
                    alt={rp.name}
                    onError={(e) => { e.currentTarget.src = 'https://placehold.co/600x800/f5f5f4/a8a29e?text=Image+Unavailable'; }}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 rounded-xl"
                  />
                  {rp.stock === 0 && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-xl">
                      <span className="text-white text-xs uppercase tracking-widest">Out of Stock</span>
                    </div>
                  )}
                </div>
                <h3 className="text-base md:text-lg font-medium mb-1 line-clamp-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {rp.name}
                </h3>
                <p className="text-sm font-light">₹{rp.price?.toLocaleString('en-IN')}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Reviews */}
      <section className="mt-20 md:mt-24 border-t border-border pt-16" data-testid="reviews-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Existing reviews */}
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Community</p>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Customer Reviews
            </h2>
            {reviews.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                No reviews yet. Be the first to share your experience.
              </p>
            ) : (
              <div className="space-y-6">
                {reviews.map((r) => (
                  <div key={r.id} className="border-b border-border pb-6 last:border-0" data-testid={`review-${r.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium">{r.user_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    <p className="text-sm leading-relaxed">{r.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Write a review */}
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Share</p>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Write a Review
            </h2>
            {reviewSubmitted ? (
              <div className="bg-green-50 border border-green-200 p-5 rounded-xl flex items-start gap-3" data-testid="review-success">
                <Check className="h-5 w-5 text-green-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">Thank you for sharing!</p>
                  <p className="text-xs text-green-800 mt-1">
                    Your review has been submitted and will appear after our team reviews it.
                  </p>
                </div>
              </div>
            ) : !user ? (
              <div className="border border-border rounded-xl p-6 text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Please log in to write a review.
                </p>
                <Button onClick={() => navigate('/login')} className="rounded-full uppercase tracking-widest text-xs" data-testid="review-login-btn">
                  Log In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleReviewSubmit} className="space-y-4" data-testid="review-form">
                <textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="What did you love about this piece? How does it feel? Would you buy it again?"
                  className="w-full min-h-[140px] p-4 border border-input bg-background rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  minLength={5}
                  maxLength={1000}
                  required
                  data-testid="review-textarea"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{reviewText.length}/1000</p>
                  <Button
                    type="submit"
                    disabled={reviewSubmitting || reviewText.trim().length < 5}
                    className="rounded-full uppercase tracking-widest text-xs"
                    data-testid="review-submit-btn"
                  >
                    {reviewSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</> : 'Submit Review'}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Reviews are moderated. They will appear on the site after our team approves them.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Fullscreen Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-in fade-in duration-200"
          onClick={() => setLightboxOpen(false)}
          data-testid="lightbox-overlay"
        >
          {/* Close button */}
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            className="absolute top-5 right-5 bg-white/10 hover:bg-white/20 text-white rounded-full p-2.5 transition-colors z-10"
            data-testid="lightbox-close"
            aria-label="Close fullscreen view"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Zoom controls */}
          <div className="absolute top-5 left-5 flex items-center gap-2 bg-white/10 backdrop-blur rounded-full p-1.5 z-10">
            <button
              onClick={(e) => { e.stopPropagation(); const s = Math.max(1, lightboxScale - 0.5); setLightboxScale(s); if (s === 1) setPanOffset({ x: 0, y: 0 }); }}
              className="text-white hover:bg-white/20 rounded-full w-9 h-9 flex items-center justify-center"
              data-testid="lightbox-zoom-out"
              aria-label="Zoom out"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="text-white text-xs uppercase tracking-widest px-2 min-w-[3rem] text-center">{Math.round(lightboxScale * 100)}%</span>
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxScale(Math.min(4, lightboxScale + 0.5)); }}
              className="text-white hover:bg-white/20 rounded-full w-9 h-9 flex items-center justify-center"
              data-testid="lightbox-zoom-in"
              aria-label="Zoom in"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>

          {/* Mobile hint */}
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur text-white text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full md:hidden pointer-events-none">
            Pinch or double-tap to zoom
          </div>

          {/* Main image */}
          <div
            className="relative max-w-[92vw] max-h-[92vh] overflow-hidden touch-none"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
          >
            <img
              src={currentImage}
              alt={product.name}
              className="transition-transform duration-100 ease-out select-none max-w-full max-h-[92vh] object-contain"
              style={{ transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${lightboxScale})`, transformOrigin: 'center center' }}
              draggable={false}
              data-testid="lightbox-image"
            />
          </div>

          {/* Prev/Next in lightbox */}
          {allImages.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); resetLightbox(); setSelectedImage((selectedImage - 1 + allImages.length) % allImages.length); }}
                className="absolute left-5 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors"
                data-testid="lightbox-prev"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); resetLightbox(); setSelectedImage((selectedImage + 1) % allImages.length); }}
                className="absolute right-5 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors"
                data-testid="lightbox-next"
                aria-label="Next image"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur text-white text-xs px-4 py-1.5 rounded-full">
                {selectedImage + 1} / {allImages.length}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetail;